import { useMemo, useState, type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ANCIENNETE_ALERTE_JOURS,
  DISCREPANCY_STATUS_LABELS,
  COPACK_FILTRES,
  COPACK_FILTRE_LABELS,
  adresseEcart,
  type CopackFiltre,
  type DiscrepancyRow,
  type DiscrepancyScope,
  type ListDiscrepanciesQueryInput,
} from '@kn/shared';
import { api } from '../api/client';
import { messageErreur } from '../api/error-message';
import { BadgeCopack } from '../components/BadgeCopack';
import { BadgeEvolution } from '../components/BadgeEvolution';
import { classeMontant } from '../components/montant';
import { LegendeEcart } from '../components/LegendeEcart';

const ECARTS_QUERY_KEY = ['discrepancies'] as const;

function formaterNombre(valeur: string): string {
  const nombre = Number(valeur);
  if (!Number.isFinite(nombre)) return valeur;
  return nombre.toLocaleString('fr-FR', { maximumFractionDigits: 3 });
}

function formaterDate(iso: string | null): string {
  if (!iso) return '—';
  const [annee, mois, jour] = iso.split('-');
  return jour && mois && annee ? `${jour}/${mois}/${annee}` : iso;
}

export function EcartsPage(): ReactElement {
  const [maille, setMaille] = useState<DiscrepancyScope>('ARTICLE');
  const [recherche, setRecherche] = useState('');
  const [clesSaisies, setClesSaisies] = useState('');
  const [inclureSoldes, setInclureSoldes] = useState(false);
  const [inclureHorsControle, setInclureHorsControle] = useState(false);
  const [inclureValides, setInclureValides] = useState(false);
  const [copack, setCopack] = useState<CopackFiltre | ''>('');
  const [instruction, setInstruction] = useState<'' | 'COMMENTES' | 'EVOLUES'>('');
  const [commentaireType, setCommentaireType] = useState('');
  const filtres: ListDiscrepanciesQueryInput = useMemo(
    () => ({
      maille,
      ...(recherche.trim() ? { search: recherche.trim() } : {}),
      ...(clesSaisies.trim()
        ? { cles: clesSaisies.split(/[\s,;]+/).filter((cle) => cle.length > 0) }
        : {}),
      inclureSoldes,
      inclureHorsControle,
      inclureValides,
      ...(copack ? { copack } : {}),
      ...(instruction === 'COMMENTES' ? { avecCommentaires: true } : {}),
      ...(instruction === 'EVOLUES' ? { evoluesAuDernierImport: true } : {}),
      ...(commentaireType ? { commentaireType } : {}),
    }),
    [
      maille,
      recherche,
      clesSaisies,
      inclureSoldes,
      inclureHorsControle,
      inclureValides,
      copack,
      instruction,
      commentaireType,
    ],
  );

  const typesCommentaire = useQuery({
    queryKey: ['comment-types', 'actifs'],
    queryFn: () => api.commentTypes(true),
    staleTime: 5 * 60 * 1000,
  });

  const ecarts = useQuery({
    queryKey: [...ECARTS_QUERY_KEY, filtres],
    queryFn: () => api.listDiscrepancies(filtres),
  });

  const lignes: DiscrepancyRow[] = ecarts.data ?? [];
  const anciens = lignes.filter((l) => l.ancienneteJours > ANCIENNETE_ALERTE_JOURS).length;

  return (
    <>
      <h1>Écarts à date</h1>

      <p className="bandeau-avertissement" role="note">
        <strong>Avant application des règles de neutralisation</strong>
        <span>
          Un écart naît le jour où le cumul quitte zéro et se solde le jour où il y revient. Sa date
          d&apos;apparition ne bouge pas tant qu&apos;il vit ; un écart réapparu après
          régularisation compte comme une nouvelle apparition.
        </span>
      </p>

      <div className="carte">
        <div className="barre-actions barre-actions--titre">
          <h2>Filtres</h2>
          <div className="barre-actions__boutons">
            <a className="bouton bouton--secondaire" href={api.discrepanciesExportUrl(filtres)}>
              Exporter l&apos;état en Excel
            </a>
          </div>
        </div>
        <div className="barre-actions">
          <label className="champ" htmlFor="ecarts-maille">
            <span>Maille</span>
            <select
              id="ecarts-maille"
              value={maille}
              onChange={(event) => {
                setMaille(event.target.value as DiscrepancyScope);
              }}
            >
              <option value="ARTICLE">Article</option>
              <option value="FAMILLE">Famille d&apos;équivalence</option>
            </select>
          </label>
          <label className="champ" htmlFor="ecarts-recherche">
            <span>Rechercher</span>
            <input
              id="ecarts-recherche"
              type="search"
              placeholder="Code ou libellé"
              value={recherche}
              onChange={(event) => setRecherche(event.target.value)}
            />
          </label>
          <label className="champ champ--large" htmlFor="ecarts-cles">
            <span>Instruire plusieurs articles</span>
            <input
              id="ecarts-cles"
              type="text"
              placeholder="435173 436110 429359"
              value={clesSaisies}
              onChange={(event) => setClesSaisies(event.target.value)}
            />
          </label>
          <label className="champ" htmlFor="ecarts-soldes">
            <span>Écarts soldés</span>
            <select
              id="ecarts-soldes"
              value={inclureSoldes ? 'oui' : 'non'}
              onChange={(event) => setInclureSoldes(event.target.value === 'oui')}
            >
              <option value="non">Masqués</option>
              <option value="oui">Affichés</option>
            </select>
          </label>
          <label className="champ" htmlFor="ecarts-valides">
            <span>Écarts validés</span>
            <select
              id="ecarts-valides"
              value={inclureValides ? 'oui' : 'non'}
              onChange={(event) => setInclureValides(event.target.value === 'oui')}
            >
              <option value="non">Masqués</option>
              <option value="oui">Affichés</option>
            </select>
          </label>
          <label className="champ" htmlFor="ecarts-copack">
            <span>Copacking</span>
            <select
              id="ecarts-copack"
              value={copack}
              onChange={(event) => setCopack(event.target.value as CopackFiltre | '')}
            >
              <option value="">Tous</option>
              {COPACK_FILTRES.map((valeur) => (
                <option key={valeur} value={valeur}>
                  {COPACK_FILTRE_LABELS[valeur]}
                </option>
              ))}
            </select>
          </label>
          <label className="champ" htmlFor="ecarts-instruction">
            <span>Instruction</span>
            <select
              id="ecarts-instruction"
              value={instruction}
              onChange={(event) =>
                setInstruction(event.target.value as '' | 'COMMENTES' | 'EVOLUES')
              }
            >
              <option value="">Tous</option>
              <option value="EVOLUES">Évolués au dernier import</option>
              <option value="COMMENTES">Avec commentaires</option>
            </select>
          </label>
          <label className="champ" htmlFor="ecarts-commentaire-type">
            <span>Dernier commentaire</span>
            <select
              id="ecarts-commentaire-type"
              value={commentaireType}
              onChange={(event) => setCommentaireType(event.target.value)}
            >
              <option value="">Tous types</option>
              {(typesCommentaire.data ?? []).map((type) => (
                <option key={type.id} value={type.libelle}>
                  {type.libelle}
                </option>
              ))}
            </select>
          </label>
          <label className="champ" htmlFor="ecarts-hors-controle">
            <span>Articles hors périmètre</span>
            <select
              id="ecarts-hors-controle"
              value={inclureHorsControle ? 'oui' : 'non'}
              onChange={(event) => setInclureHorsControle(event.target.value === 'oui')}
            >
              <option value="non">Masqués</option>
              <option value="oui">Affichés</option>
            </select>
          </label>
        </div>

        {ecarts.isPending && <p className="chargement">Chargement des écarts…</p>}
        {ecarts.isError && <p className="alerte alerte--erreur">{messageErreur(ecarts.error)}</p>}

        {ecarts.data && lignes.length === 0 && (
          <p className="alerte alerte--info" role="status">
            Aucun écart sur ce filtre. Importez les sources depuis l&apos;écran Imports, ou
            élargissez le périmètre.
          </p>
        )}

        {ecarts.data && lignes.length > 0 && (
          <>
            <div className="totaux">
              <div>
                <span>Écarts affichés</span>
                <strong>{lignes.length.toLocaleString('fr-FR')}</strong>
              </div>
              <div>
                <span>Au-delà de J+{ANCIENNETE_ALERTE_JOURS}</span>
                <strong className={anciens > 0 ? 'nombre nombre--alerte' : 'nombre'}>
                  {anciens.toLocaleString('fr-FR')}
                </strong>
              </div>
              <div>
                <span>Plus ancienne apparition</span>
                <strong>{formaterDate(lignes[0]?.apparuLe ?? null)}</strong>
              </div>
            </div>

            <LegendeEcart variante="courte" />

            <table className="tableau">
              <caption className="sous-titre">
                Du plus ancien au plus récent : c&apos;est l&apos;ancienneté qui commande
                l&apos;instruction, pas le montant.
              </caption>
              <thead>
                <tr>
                  <th scope="col">Clé</th>
                  <th scope="col">Libellé</th>
                  <th scope="col">Copack</th>
                  <th scope="col">Dernier import</th>
                  <th scope="col">Instruction</th>
                  <th scope="col">Apparu le</th>
                  <th scope="col">Ancienneté</th>
                  <th scope="col">Écart (UVC)</th>
                  <th scope="col">Reste à instruire</th>
                  <th scope="col">Statut</th>
                  <th scope="col">Fiche</th>
                </tr>
              </thead>
              <tbody>
                {lignes.map((ligne) => (
                  <tr key={ligne.id}>
                    <td>
                      {ligne.maille === 'FAMILLE' ? (
                        <>
                          <span className="etiquette">Famille</span>{' '}
                          {ligne.codpros.join(' / ')}
                        </>
                      ) : (
                        ligne.cle
                      )}
                    </td>
                    <td>{ligne.libelle}</td>
                    <td>
                      <BadgeCopack role={ligne.copack} />
                    </td>
                    <td>
                      <BadgeEvolution evolution={ligne.derniereEvolution} />
                    </td>
                    <td>
                      {ligne.dernierCommentaire ? (
                        <span
                          className="marque-commentaire"
                          title={`${ligne.dernierCommentaire.total} commentaire(s) — dernier le ${formaterDate(ligne.dernierCommentaire.createdAt.slice(0, 10))}`}
                        >
                          <span aria-hidden="true">💬</span>
                          <span className="etiquette">{ligne.dernierCommentaire.typeLibelle}</span>
                          {ligne.dernierCommentaire.total > 1 && (
                            <span className="marque-commentaire__compte">
                              ×{ligne.dernierCommentaire.total}
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="copack-vide">—</span>
                      )}
                    </td>
                    <td>
                      {formaterDate(ligne.apparuLe)}
                      {ligne.premierMembreApparuLe &&
                        ligne.premierMembreApparuLe !== ligne.apparuLe && (
                          <div className="sous-titre">
                            1er membre : {formaterDate(ligne.premierMembreApparuLe)}
                          </div>
                        )}
                    </td>
                    <td
                      className={
                        ligne.ancienneteJours > ANCIENNETE_ALERTE_JOURS
                          ? 'nombre nombre--alerte'
                          : 'nombre'
                      }
                    >
                      {ligne.ancienneteJours} j
                    </td>
                    <td className={`droite ${classeMontant(ligne.ecartUvc)}`}>
                      {formaterNombre(ligne.ecartUvc)}
                    </td>
                    <td className={`droite ${classeMontant(ligne.ecartResiduel)}`}>
                      {formaterNombre(ligne.ecartResiduel)}
                    </td>
                    <td>
                      <span
                        className={
                          ligne.statut === 'ACTIF'
                            ? 'etiquette etiquette--attente'
                            : 'etiquette etiquette--actif'
                        }
                      >
                        {DISCREPANCY_STATUS_LABELS[ligne.statut]}
                      </span>
                    </td>
                    <td>
                      {/* Un seul bouton, une seule destination : la fiche porte
                          le jour par jour et l'instruction. Nouvel onglet, pour
                          ne pas perdre la liste et ses filtres. */}
                      <a
                        className="bouton bouton--secondaire"
                        href={`/ecarts/${adresseEcart(ligne.maille, ligne.cle)}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Ouvrir
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>

    </>
  );
}
